import { forwardRef, useRef, useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { t, tf, type Lang, type TranslationKey } from '../../lib/i18n'
import { useOpenOnce } from '../../lib/in-app-history'
import { clearDraft, readDraft, writeDraft } from '../../lib/route-draft'
import { formatOrderId } from '../../lib/orders-api'
import type { HopQueueItem, HopQueueOrderSummary, HoldChoice, SnoozePreset } from '../../types'
import { ConfirmDialog } from '../orders/ConfirmDialog'
import { fetchSwitchPreview, type SwitchPreview } from '../../lib/switch-preview'

/** bench-HOLD-CARD-HAS-ITS-BUTTONS.CB-E §1.2 — which buttons a card shows. */
export type CardAction = 'approve' | HoldChoice

const SWITCH_ROLES = ['hop', 'owner', 'architect']

// bench-SWITCH-SAYS-WHAT-MOVES.CB-E §1.6: the card shows where the order sits NOW and
// offers exactly one Switch, to the other entity -- never both, never a guess for an
// unknown/null entitySlug.
// Rook #66231: Confirm and Switch belong to the invoice hold only, whose sourceRef is the bare
// order id. A suffixed sourceRef ('<order>:<kind>', e.g. ':payment_hold') is another card on
// the same order and takes no choice, so it draws Handled like every other card.
export function holdCardActions(item: HopQueueItem, roles: string[]): CardAction[] {
  if (item.domain !== 'odoo_sync' || (item.sourceRef ?? '').includes(':')) return ['approve']
  const actions: CardAction[] = ['confirm']
  if (roles.some((r) => SWITCH_ROLES.includes(r))) {
    if (item.entitySlug === 'TKF') actions.push('switch_zgf')
    else if (item.entitySlug === 'ZGF') actions.push('switch_tkf')
  }
  return actions
}

type SwitchPreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: SwitchPreview }
  | { status: 'error' }

/**
 * Per-card outcome of an operator action (bench §3.2).
 *
 * A card is removed only on a landed write; every state below means the write
 * did NOT land and the card is still on screen saying so. `queued` is the
 * offline case and is deliberately distinct from `failed`.
 */
export type ActionState = 'queued' | 'failed' | 'conflict' | 'expired'

/**
 * Bench §3.2 — the card stays put unless the write landed, so it has to say
 * WHY it is still here. `queued` is not a failure and is not styled as one.
 */
const ACTION_LABEL: Record<ActionState, TranslationKey> = {
  queued: 'actionQueued',
  failed: 'actionFailed',
  conflict: 'actionConflict',
  expired: 'actionExpired',
}

const ACTION_CLASS: Record<ActionState, string> = {
  queued: 'bg-secondary text-secondary-foreground border-border',
  failed: 'bg-destructive/10 text-destructive border-destructive/30',
  conflict: 'bg-amber-50 text-amber-800 border-amber-200',
  expired: 'bg-muted text-muted-foreground border-border',
}

// ux1 (d), Rook #63836/#63841: card actions are 48 px (the floor is 44), Handled/Confirm in
// the navy primary token, Snooze/Switch outlined with navy text. Rook #66212: the plain close
// (resolve with no choice) reads Handled, pending Marking; hold cards keep Confirm and Switch.
const ACTION_BUTTON =
  'min-h-[48px] rounded-lg px-4 text-base font-semibold disabled:opacity-40 transition-colors'
const PRIMARY_ACTION = 'bg-primary text-primary-foreground hover:bg-primary/90'
const SECONDARY_ACTION = 'border border-border bg-card text-primary hover:bg-muted hover:border-input'

const TIER_STRIPE: Record<string, string> = {
  now: 'bg-red-500',
  today: 'bg-amber-400',
  closing: 'bg-blue-400',
}

const TIER_BADGE: Record<string, string> = {
  now: 'bg-red-100 text-red-700',
  today: 'bg-amber-100 text-amber-700',
  closing: 'bg-blue-100 text-blue-700',
}

// R482 (b): the tier badge reads the tier headings' own keys (it used to draw NOW/TODAY/CLOSING
// in English in both languages). A tier outside the three keeps its raw name, as before.
const TIER_LABEL: Record<string, TranslationKey> = {
  now: 'tierNow',
  today: 'tierToday',
  closing: 'tierClosing',
}

// R482 (b), Salma #66120: the order's date and the alert's time are written out as date and clock
// in Beirut ("2 Nov, 06:14"), whatever the device's own zone. Shaped with en-GB in both languages,
// like every timestamp in HOP (features/orders/format.ts), so digits never flip under Arabic.
const BEIRUT_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Beirut',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

export function formatBeirut(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '\u2014'
  const p: Record<string, string> = {}
  for (const part of BEIRUT_PARTS.formatToParts(d)) p[part.type] = part.value
  return `${p.day} ${p.month}, ${p.hour}:${p.minute}`
}

// R482 (b): Back from an order opened on a card returns to the cockpit where it was. The position
// is saved as a route draft (lib/route-draft.ts: this tab's sessionStorage, cleared with every draft
// on sign-out by shell-clear.ts) when a card opens its order, and restored once, after the list has
// loaded again (ActivityFeed).
export const COCKPIT_PATH = '/cockpit'
const SCROLL_SCOPE = 'scroll'

export function saveCockpitScroll(): void {
  writeDraft(COCKPIT_PATH, SCROLL_SCOPE, window.scrollY)
}

export function restoreCockpitScroll(): void {
  const y = readDraft<number>(COCKPIT_PATH, SCROLL_SCOPE)
  if (typeof y !== 'number') return
  clearDraft(COCKPIT_PATH, SCROLL_SCOPE)
  window.scrollTo(0, y)
}

// The line's name in the card's language: Arabic falls back to English, as the new-order form's
// product names do (NewOrderScreen.tsx).
function lineName(line: HopQueueOrderSummary['firstItems'][number], lang: Lang): string {
  return (lang === 'ar' ? line.nameAr ?? line.nameEn : line.nameEn ?? line.nameAr) ?? ''
}

const FULFIL_LABEL: Record<string, TranslationKey> = {
  delivery: 'fulfilDelivery',
  pickup: 'fulfilPickup',
}

interface OrderOpenLinkProps {
  order: HopQueueOrderSummary
  code: string
  label: string
}

// The card's one link (R482 (b)): the TKF code, an <a> to /orders/:id with the code and the
// customer as its name. A tap anywhere else on the card clicks it (QueueItem below), so the whole
// card is the target while the keyboard has a single, real link to reach. useOpenOnce keeps one
// activation to one history entry (in-app-history.ts).
const OrderOpenLink = forwardRef<HTMLAnchorElement, OrderOpenLinkProps>(function OrderOpenLink({ order, code, label }, ref) {
  const to = `/orders/${order.orderId}`
  const openOnce = useOpenOnce(to)
  return (
    <Link
      ref={ref}
      to={to}
      data-card-open
      aria-label={label}
      dir="ltr"
      onClick={(e) => {
        saveCockpitScroll()
        openOnce(e)
      }}
      className="inline-flex min-h-[44px] items-center rounded-sm text-base font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {code}
    </Link>
  )
})

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const s = Math.floor(ms / 1000)
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

interface Props {
  item: HopQueueItem
  lang: Lang
  /** Roles of the signed-in operator — gates Switch per holdCardActions (§1.2). */
  roles?: string[]
  /** Set when a submitted action did NOT land; undefined when there is nothing to report. */
  actionState?: ActionState
  onResolve?: (id: string, choice?: HoldChoice) => Promise<void>
  onSnooze?: (id: string, preset: SnoozePreset) => Promise<void>
}

export function QueueItem({ item, lang, roles = [], actionState, onResolve, onSnooze }: Props) {
  const [resolving, setResolving] = useState(false)
  const [snoozeOpen, setSnoozeOpen] = useState(false)
  const [snoozingPreset, setSnoozingPreset] = useState<SnoozePreset | null>(null)
  const [switchPreviewState, setSwitchPreviewState] = useState<SwitchPreviewState>({ status: 'idle' })

  const snoozeOptions: { value: SnoozePreset; label: string }[] = [
    { value: '1hr', label: t(lang, 'snooze1hr') },
    { value: '4hr', label: t(lang, 'snooze4hr') },
    { value: 'tomorrow_morning', label: t(lang, 'snoozeTomorrow') },
  ]

  const stripeClass = TIER_STRIPE[item.hopUrgencyTier] ?? 'bg-amber-400'
  const badgeClass = TIER_BADGE[item.hopUrgencyTier] ?? 'bg-amber-100 text-amber-700'
  const tierKey = TIER_LABEL[item.hopUrgencyTier]
  const tierLabel = tierKey ? t(lang, tierKey) : item.hopUrgencyTier.toUpperCase()
  const actions = holdCardActions(item, roles)
  // R482 (b): a card about an order names it and opens it; a card with no order (null, or a server
  // from before the join) draws no order line, no link and opens nothing.
  const order = item.order ?? null
  const openRef = useRef<HTMLAnchorElement>(null)
  const orderCode = order ? formatOrderId(order.orderId, order.shortCode) : ''
  const openLabel = order ? [orderCode, order.customerName].filter(Boolean).join(', ') : ''
  const whatLine = order
    ? [
        order.itemCount === 1 ? t(lang, 'itemsOne') : tf(lang, 'items', { count: order.itemCount }),
        order.firstItems.slice(0, 2).map((line) => lineName(line, lang)).filter(Boolean).join(', '),
        order.fulfillmentMethod && FULFIL_LABEL[order.fulfillmentMethod] ? t(lang, FULFIL_LABEL[order.fulfillmentMethod]) : '',
      ]
        .filter(Boolean)
        .join(' \u00b7 ')
    : ''

  // A tap anywhere on the card opens its order, except on its controls: each button stops its
  // click here (own() below). A click from a dialog the card portals out (Switch) is not inside
  // the card's DOM and is ignored; a click on the link itself is the link's own.
  function openFromCard(e: MouseEvent<HTMLDivElement>) {
    const link = openRef.current
    if (!link) return
    const target = e.target as Node
    if (!e.currentTarget.contains(target) || link.contains(target)) return
    link.click()
  }

  // Every control on the card does its own job and never opens the order.
  function own(fn: () => void) {
    return (e: MouseEvent) => {
      e.stopPropagation()
      fn()
    }
  }
  // §1.3 — a queued card's own tap already landed in the queue; a second tap would
  // enqueue a duplicate action under the same card. Every other state's buttons work.
  const actionsDisabled = resolving || snoozingPreset !== null || actionState === 'queued'
  // Rook #66086: the server refuses a snooze on a NOW item (400), so a NOW card draws no Snooze.
  const canSnooze = item.hopUrgencyTier !== 'now'

  async function handleResolve(choice?: HoldChoice) {
    if (!onResolve || resolving) return
    setResolving(true)
    try {
      if (choice) {
        await onResolve(item.id, choice)
      } else {
        await onResolve(item.id)
      }
    } finally {
      setResolving(false)
    }
  }

  async function handleSwitchDialogOpenChange(choice: 'switch_tkf' | 'switch_zgf', open: boolean) {
    if (!open) return
    setSwitchPreviewState({ status: 'loading' })
    try {
      const data = await fetchSwitchPreview(item.id, choice === 'switch_tkf' ? 'tkf' : 'zgf')
      setSwitchPreviewState({ status: 'success', data })
    } catch {
      setSwitchPreviewState({ status: 'error' })
    }
  }

  function switchDialogDescription(choice: 'switch_tkf' | 'switch_zgf'): string {
    const toEntity = choice === 'switch_tkf' ? 'TKF' : 'ZGF'
    if (switchPreviewState.status === 'error') {
      return tf(lang, 'switchCountFailed', { entity: toEntity })
    }
    if (switchPreviewState.status !== 'success') {
      return t(lang, 'refreshing')
    }
    const { data } = switchPreviewState
    const name = data.customerName ?? data.masterCustomerId
    if (data.alreadyThere) {
      return tf(lang, 'switchAlreadyThere', { name, entity: data.toEntity })
    }
    if (data.otherOrdersMoving === 0) {
      return tf(lang, 'switchMovesNoOthers', { name, entity: data.toEntity })
    }
    if (data.otherOrdersMoving === 1) {
      return tf(lang, 'switchMovesOne', { name, entity: data.toEntity })
    }
    return tf(lang, 'switchMoves', { name, entity: data.toEntity, count: data.otherOrdersMoving })
  }

  async function handleSnooze(preset: SnoozePreset) {
    if (!onSnooze) return
    setSnoozeOpen(false)
    setSnoozingPreset(preset)
    try {
      await onSnooze(item.id, preset)
    } finally {
      setSnoozingPreset(null)
    }
  }

  if (item.actionRequired) {
    return (
      <div
        data-card
        data-card-opens={order ? 'true' : undefined}
        onClick={order ? openFromCard : undefined}
        className={`bg-card rounded-xl border border-border shadow-sm overflow-hidden${
          order ? ' cursor-pointer hover:border-input hover:shadow-md transition-shadow' : ''
        }`}
      >
        <div className={`h-1 w-full ${stripeClass}`} />
        <div className="p-4">
          <div data-card-row className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
            <div className="flex-1 min-w-0">
              {/* R482 (b), Rook #66858: the fields in this order: code, customer, what the order is,
                  the issue, the order's date and the alert's time, then the tier and age badges. */}
              {order && (
                <>
                  <p data-field="code" className="leading-none">
                    <OrderOpenLink ref={openRef} order={order} code={orderCode} label={openLabel} />
                  </p>
                  {order.customerName && (
                    <p data-field="customer" className="text-base font-medium text-foreground leading-snug">
                      {order.customerName}
                    </p>
                  )}
                  <p data-field="what" className="text-sm text-foreground mt-0.5 leading-relaxed">
                    {whatLine}
                  </p>
                </>
              )}
              <p data-field="title" className={`text-base font-semibold text-foreground leading-snug${order ? ' mt-2' : ''}`}>
                {item.title}
              </p>
              {item.body && (
                <p data-field="body" className="text-sm text-muted-foreground mt-1 leading-relaxed">{item.body}</p>
              )}
              <p data-field="times" className="flex flex-wrap gap-x-3 text-xs text-muted-foreground mt-1">
                {order && (
                  <span data-time="ordered">
                    {t(lang, 'cardOrdered')} <span dir="ltr">{formatBeirut(order.placedAt)}</span>
                  </span>
                )}
                <span data-time="alert">
                  {t(lang, 'cardAlert')} <span dir="ltr">{formatBeirut(item.createdAt)}</span>
                </span>
              </p>
              <div data-field="badges" className="flex flex-wrap items-center gap-1.5 mt-2">
                <span
                  data-badge="tier"
                  className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold tracking-wide ${badgeClass}`}
                >
                  {tierLabel}
                </span>
                <span
                  data-badge="age"
                  className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground tabular-nums"
                >
                  {relativeTime(item.createdAt)}
                </span>
                {item.domain === 'odoo_sync' && (
                  <span
                    data-testid="entity-badge"
                    className="inline-flex items-center rounded bg-secondary px-1.5 py-0.5 text-xs font-semibold text-secondary-foreground"
                  >
                    {item.entitySlug === 'TKF' || item.entitySlug === 'ZGF' ? item.entitySlug : '?'}
                  </span>
                )}
                {item.stale && (
                  <span
                    data-testid="stale-marker"
                    className="inline-flex items-center rounded border border-input px-1.5 py-0.5 text-xs font-semibold text-muted-foreground"
                  >
                    STALE
                  </span>
                )}
              </div>
              {actionState && (
                <p
                  role="status"
                  data-action-state={actionState}
                  className={`mt-2 inline-flex items-center rounded border px-2 py-1 text-xs font-medium ${ACTION_CLASS[actionState]}`}
                >
                  {t(lang, ACTION_LABEL[actionState])}
                </p>
              )}
            </div>

            <div data-card-actions className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:pt-0.5">
              {actions.includes('approve') && (
                <button
                  onClick={own(() => handleResolve())}
                  disabled={actionsDisabled}
                  className={`${ACTION_BUTTON} ${PRIMARY_ACTION} flex-1 sm:flex-none`}
                >
                  {resolving ? t(lang, 'marking') : t(lang, 'handled')}
                </button>
              )}

              {actions.includes('confirm') && (
                <button
                  onClick={own(() => handleResolve('confirm'))}
                  disabled={actionsDisabled}
                  className={`${ACTION_BUTTON} ${PRIMARY_ACTION} flex-1 sm:flex-none`}
                >
                  {resolving ? t(lang, 'approving') : t(lang, 'confirmHold')}
                </button>
              )}

              {canSnooze && (
              <div className="relative flex-1 sm:flex-none">
                <button
                  onClick={own(() => setSnoozeOpen((o) => !o))}
                  disabled={actionsDisabled}
                  className={`${ACTION_BUTTON} ${SECONDARY_ACTION} w-full`}
                >
                  {snoozingPreset ? t(lang, 'snoozing') : t(lang, 'snooze')}
                </button>
                {snoozeOpen && (
                  <div className="absolute end-0 top-full mt-1 z-20 w-44 rounded-md border border-border bg-card shadow-lg">
                    {snoozeOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={own(() => handleSnooze(opt.value))}
                        className="block w-full min-h-[44px] text-start px-3 py-2 text-sm text-foreground hover:bg-muted first:rounded-t-md last:rounded-b-md transition-colors"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              )}

              {(['switch_tkf', 'switch_zgf'] as const).map(
                (choice) =>
                  actions.includes(choice) && (
                    <ConfirmDialog
                      key={choice}
                      trigger={
                        <button
                          onClick={own(() => {})}
                          disabled={actionsDisabled}
                          className={`${ACTION_BUTTON} ${SECONDARY_ACTION} flex-1 sm:flex-none`}
                        >
                          {t(lang, choice === 'switch_tkf' ? 'switchToTkf' : 'switchToZgf')}
                        </button>
                      }
                      title={t(lang, choice === 'switch_tkf' ? 'switchToTkf' : 'switchToZgf')}
                      description={switchDialogDescription(choice)}
                      actionLabel={t(lang, choice === 'switch_tkf' ? 'switchToTkf' : 'switchToZgf')}
                      onOpenChange={(open) => handleSwitchDialogOpenChange(choice, open)}
                      onConfirm={() => handleResolve(choice)}
                    />
                  ),
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const dotClass =
    item.hopUrgencyTier === 'now'
      ? 'bg-red-400'
      : item.hopUrgencyTier === 'today'
        ? 'bg-amber-400'
        : 'bg-muted-foreground/40'

  // One row of the "Happening now" list; the list card (ActivityFeed) draws the border.
  return (
    <div data-feed-row className="flex items-start gap-3 py-3">
      <span className="text-sm text-muted-foreground tabular-nums shrink-0 min-w-[4.5rem]">
        {relativeTime(item.createdAt)}
      </span>
      <span className={`mt-2 h-2 w-2 rounded-full shrink-0 ${dotClass}`} />
      <div className="flex-1 min-w-0">
        <p className="text-base text-foreground leading-snug">{item.title}</p>
        {item.body && (
          <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">{item.body}</p>
        )}
      </div>
      {item.stale && (
        <span
          data-testid="stale-marker"
          className="inline-flex shrink-0 items-center rounded border border-input px-1.5 py-0.5 text-xs font-semibold text-muted-foreground"
        >
          STALE
        </span>
      )}
    </div>
  )
}
